'use client';

import React from 'react';
import Share from '@/components/icons/share';
import Download from '@/components/icons/download';
import Print from '@/components/icons/print';
import useMediaQuery from '@/helpers/hooks/useMediaQuery';
import { desktop } from '@/helpers/utils/screensizes';
import useAccount from '@/lib/hooks/useAccount';
import { useSearchParams } from 'next/navigation';

interface SummaryBannerProps {
    onShare: () => void;
    onDownload: () => void;
    onPrint: () => void;
    data: any;
    projectFormData: any;
    allowShare: boolean;
}

const InfoRow = ({ label, value }: { label: string; value: string; }) => (
    <>
        <p className="text-sm">{label}</p>
        <p className="text-2xl print:text-xl">{value || 'N/A'}</p>
        <div className="my-[2px] border-b border-white" />
    </>
);

const SummaryBanner: React.FC<SummaryBannerProps> = ({
    onShare,
    onDownload,
    onPrint,
    data,
    projectFormData,
    allowShare,
}) => {
    const [isDesktopSize] = useMediaQuery(desktop);
    const { account } = useAccount();
    const searchParams = useSearchParams();

    const companyName = searchParams.get('company');
    const {
        projectName,
        projectLocation,
        projectType,
        createdAt,
        username,
        customerName,
    } = projectFormData;

    const imageUrl = data?.dataSource?.sections?.[0]?.section_with_banner?.banner_image?.url;

    const formatDate = (date: string | undefined) =>
        date
            ? new Date(date).toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
            })
            : 'N/A';

    const buttons = [
        { text: 'Share', onClick: onShare, icon: <Share /> },
        { text: 'Save', onClick: onDownload, icon: <Download /> },
        { text: 'Print', onClick: onPrint, icon: <Print />, className: 'hidden xl:flex' },
    ];
    const infoFields = [
        { label: 'Project Type', value: () => projectType?.name },
        { label: 'Location', value: () => projectLocation?.name },
        { label: 'Date Created', value: () => formatDate(createdAt) },
        { label: 'Prepared by', value: () => username || '-' },
    ];
    return (
        <div
            style={{ backgroundImage: `url(${imageUrl})` }}
            className={`product-summary print:origin-start flex break-after-page print:h-[90vh] print:shadow-none ${isDesktopSize
                ? 'h-[80vh] w-full bg-cover bg-center flex-row justify-between'
                : 'w-full md:h-3/4 print:h-[90vh] sm:h-3/4 bg-cover bg-center flex-col justify-start overflow-hidden'
                }`}
        >
            <div className="summary-head-left space-y-[2px] p-[10px] text-white print:space-y-[0px] md:p-[20px] lg:p-[30px]">
                <h1 className="text-3xl font-semibold print:text-2xl">{projectName}</h1>
                    <h2 className="py-[10px] m-0 text-lg font-light print:py-0 print:text-md">
                        by {account?.companyName || companyName || ''}
                    </h2>
                {!allowShare && (
                    <div className="flex flex-wrap gap-[8px] print:hidden" data-html2canvas-ignore>
                        {buttons.map(({ text, icon, onClick, className }) => (
                            <button
                                key={text}
                                onClick={onClick}
                                className={`min-w-[90px] items-center cursor-pointer justify-center gap-[4px] bg-white p-[8px] text-sm text-black hover:bg-gray-500 ${className || 'flex'}`}
                            >
                                {icon}
                                {text}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            <div className="p-[10px] text-white print:mt-0 print:w-full print:p-[20px] sm:w-full md:p-[20px] lg:w-[50%] lg:p-[30px]">
                {infoFields.map(({ label, value }) => (
                    <InfoRow key={label} label={label} value={value()} />
                ))}
            </div>
        </div>
    );
};

export default SummaryBanner;
